import { DiscountCouponResponse } from 'api/coupon';
import {
    isProductPriceObj,
    ProductPriceObj,
} from 'api/product/getPersonalizedProductDetails/types';
import { useConfig } from 'config/use-config';
import { useCommon } from 'contexts/common';
import { isSubscriptionItem, PdProductItem } from 'interfaces/line-item';
import { useCallback, useMemo } from 'react';

interface UseSubscriptionDiscount {
  couponPrices?: DiscountCouponResponse | ProductPriceObj;
  item?: PdProductItem;
  items?: PdProductItem[];
  selectedVariantId?: number | null;
}

interface SubscriptionDiscountResult {
  singleProductDiscount: number;
  multipleItemsDiscount: number;
}

/**
 * Custom hook to calculate the subscription discount for a single subscription product
 * or the average discount for multiple subscription products
 */
const useSubscriptionDiscount = ({
  couponPrices,
  item,
  items,
  selectedVariantId,
}: UseSubscriptionDiscount): SubscriptionDiscountResult => {
  const { globalDiscount } = useCommon();
  const { subscriptionDiscount } = useConfig();

  const defaultDiscount =
    globalDiscount.discount?.firstSubscriptionOrderDiscount || subscriptionDiscount;

  const calculateDiscount = useCallback(
    (productItem?: PdProductItem): number => {
      return productItem?.subscriptionDiscount || defaultDiscount;
    },
    [defaultDiscount]
  );

  // Calculate the subscription discount for a single product
  const singleProductDiscount = useMemo(() => {
    let variant: string | number | undefined | null;
    if (selectedVariantId && isProductPriceObj(couponPrices)) {
      variant = couponPrices.variants.find((cp) => cp.id === selectedVariantId + '')?.subscription
        ?.discounts[0].value;
    } else if (couponPrices && !isProductPriceObj(couponPrices)) {
      variant = couponPrices?.discountedVariants.find(
        (cp) => cp.variantId === selectedVariantId
      )?.subscriptionDiscount;
    }

    if (item) {
      return calculateDiscount(item);
    }

    return variant ? +variant : defaultDiscount;
  }, [selectedVariantId, couponPrices, item, defaultDiscount, calculateDiscount]);

  // Calculate the average subscription discount for multiple subscription products
  const multipleItemsDiscount = useMemo(() => {
    if (items) {
      const subscriptionItems = items.filter(isSubscriptionItem);
      if (subscriptionItems.length > 0) {
        const totalDiscount = subscriptionItems.reduce((acc, currItem) => {
          return acc + calculateDiscount(currItem);
        }, 0);

        return totalDiscount / subscriptionItems.length;
      }
    }

    return 0;
  }, [items, calculateDiscount]);

  return {
    singleProductDiscount,
    multipleItemsDiscount,
  };
};

export default useSubscriptionDiscount;
